/**
 * Shared utility for parsing and applying menu styleConfig to public menu pages.
 * Maps font family, font sizes, and colors from menu settings.
 */

import type React from 'react';

export interface ResolvedStyleConfig {
  fontFamily: string;
  fontSizeBase: string;
  fontSizeSmall: string;
  fontSizeMedium: string;
  fontSizeLarge: string;
  fontSizeXLarge: string;
  // Per-section typography (fallback to fontFamily/fontSizeBase if not set)
  fontFamilyHeader?: string;
  fontSizeHeader?: string;
  fontFamilyMacroSection?: string;
  fontSizeMacroSection?: string;
  fontFamilyCategory?: string;
  fontSizeCategory?: string;
  fontFamilyDish?: string;
  fontSizeDish?: string;
  fontFamilyDescription?: string;
  fontSizeDescription?: string;
  fontFamilyFooter?: string;
  fontSizeFooter?: string;
  fontFamilyPrice?: string;
  fontSizePrice?: string;
  fontWeightPrice?: string;
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  headerColor: string;
  categoryColor: string;
  dishColor: string;
  descriptionColor: string;
  priceColor: string;
  allergenColor: string;
  cardBackground: string;
  cardBorderColor: string;
  cardBorderWidth: string;
  showItemBorder: boolean;
  borderRadius: string;
  lineHeight: string;
  showImages: boolean;
  showPrices: boolean;
  showAllergens: boolean;
  alignment: string;
  theme: 'light' | 'dark' | 'custom';
  backgroundImage: string | null;
  categoryDisplayMode: 'cloud' | 'scroll';
  overlayColor: string;
  overlayOpacity: number; // 1-100
  logoSize?: string;
  fontWeightDish?: string;
}

// Map borderRadius preset to CSS values
const BORDER_RADIUS_MAP: Record<string, string> = {
  none: '0',
  small: '4px',
  medium: '8px',
  large: '12px',
  full: '9999px'
};

// Map fontSize preset to pixel values
const FONT_SIZE_MAP: Record<string, string> = {
  small: '14px',
  medium: '16px',
  large: '18px',
  xlarge: '20px',
  'extra-large': '22px'
};

// Default resolved config
const DEFAULT_RESOLVED: ResolvedStyleConfig = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontSizeBase: '16px',
  fontSizeSmall: '14px',
  fontSizeMedium: '16px',
  fontSizeLarge: '18px',
  fontSizeXLarge: '20px',
  primaryColor: '#1a1a1a',
  secondaryColor: '#059669',
  backgroundColor: '#ffffff',
  textColor: '#374151',
  headerColor: '#111827',
  categoryColor: '#111827',
  dishColor: '#1f2937',
  descriptionColor: '#6b7280',
  priceColor: '#059669',
  allergenColor: '#dc2626',
  cardBackground: '#f8faf9',
  cardBorderColor: '#e5e7eb',
  cardBorderWidth: '1px',
  showItemBorder: true,
  borderRadius: '12px',
  lineHeight: '1.6',
  showImages: true,
  showPrices: true,
  showAllergens: true,
  alignment: 'left',
  theme: 'light',
  backgroundImage: null,
  categoryDisplayMode: 'scroll',
  overlayColor: '#000000',
  overlayOpacity: 0,
  logoSize: 'medium',
  fontWeightDish: '700',
  fontWeightPrice: '700'
};

/**
 * Parse raw styleConfig from API (can be object or JSON string)
 */
export function parseRawConfig(raw: any): Record<string, any> {
  if (!raw) return {};
  try {
    if (typeof raw === 'string') {
      let str = raw.trim();
      if (str.startsWith('"') && str.endsWith('"')) {
        str = str.slice(1, -1).replace(/\\"/g, '"');
      }
      return JSON.parse(str);
    }
    return typeof raw === 'object' ? raw : {};
  } catch {
    return {};
  }
}

/**
 * Resolve fontSize: accepts 'small'|'medium'|'large'|'xlarge' or any CSS size (14px, 1rem, 1.2em, etc.)
 */
function resolveFontSize(value: string | undefined): string {
  if (!value) return '16px';
  const v = String(value).trim();
  if (!v) return '16px';
  const lower = v.toLowerCase();
  if (FONT_SIZE_MAP[lower]) return FONT_SIZE_MAP[lower];
  // Accept any valid CSS size: 14px, 1rem, 1.2em, 90%, 12pt, etc.
  if (/^\d+(\.\d+)?(px|rem|em|%|pt)$/i.test(v)) return v;
  if (/^\d+(\.\d+)?$/.test(v)) return `${v}px`;
  return '16px';
}

/**
 * Resolve font family - ensure proper CSS format
 */
function resolveFontFamily(value: string | undefined): string {
  if (!value) return DEFAULT_RESOLVED.fontFamily;
  const v = String(value).trim();
  if (!v) return DEFAULT_RESOLVED.fontFamily;
  // If already has quotes or commas, use as-is
  if (v.includes("'") || v.includes(',') || v.includes('"')) return v;
  return `'${v}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
}

/** Check if string is a valid CSS color (hex, rgb, hsl) - rejects labels like "Colore Sfondo" */
function isValidColor(value: string): boolean {
  const v = value.trim();
  if (v.startsWith('#') && /^#[0-9A-Fa-f]{3,8}$/.test(v)) return true;
  if (v.startsWith('rgb') && /^rgba?\([^)]+\)$/.test(v)) return true;
  if (v.startsWith('hsl') && /^hsla?\([^)]+\)$/.test(v)) return true;
  return false;
}

/**
 * Resolve color - must be valid hex or rgb; rejects labels (e.g. "Colore Sfondo")
 */
function resolveColor(value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  const v = String(value).trim();
  if (v.startsWith('#') || v.startsWith('rgb') || v.startsWith('hsl')) {
    return isValidColor(v) ? v : fallback;
  }
  const hex = `#${v.replace('#', '')}`;
  return isValidColor(hex) ? hex : fallback;
}

/**
 * Resolve styleConfig from menu API response into a consistent, usable config
 */
export function resolveStyleConfig(rawStyleConfig: any): ResolvedStyleConfig {
  const cfg = parseRawConfig(rawStyleConfig);

  const fontSize = cfg.fontSize || 'medium';
  const basePx = resolveFontSize(fontSize);
  const baseNum = parseInt(basePx, 10) || 16;
  const baseFamily = resolveFontFamily(cfg.fontFamily);

  return {
    fontFamily: baseFamily,
    fontSizeBase: basePx,
    fontSizeSmall: `${Math.max(12, baseNum - 2)}px`,
    fontSizeMedium: basePx,
    fontSizeLarge: `${baseNum + 2}px`,
    fontSizeXLarge: `${baseNum + 4}px`,
    fontFamilyHeader: cfg.fontFamilyHeader != null && String(cfg.fontFamilyHeader).trim() ? resolveFontFamily(cfg.fontFamilyHeader) : baseFamily,
    fontSizeHeader: cfg.fontSizeHeader != null && String(cfg.fontSizeHeader).trim() ? resolveFontSize(cfg.fontSizeHeader) : `${baseNum + 4}px`,
    fontFamilyMacroSection: cfg.fontFamilyMacroSection != null && String(cfg.fontFamilyMacroSection).trim() ? resolveFontFamily(cfg.fontFamilyMacroSection) : baseFamily,
    fontSizeMacroSection: cfg.fontSizeMacroSection != null && String(cfg.fontSizeMacroSection).trim() ? resolveFontSize(cfg.fontSizeMacroSection) : `${baseNum + 4}px`,
    fontFamilyCategory: cfg.fontFamilyCategory != null && String(cfg.fontFamilyCategory).trim() ? resolveFontFamily(cfg.fontFamilyCategory) : baseFamily,
    fontSizeCategory: cfg.fontSizeCategory != null && String(cfg.fontSizeCategory).trim() ? resolveFontSize(cfg.fontSizeCategory) : `${Math.max(12, baseNum - 2)}px`,
    fontFamilyDish: cfg.fontFamilyDish != null && String(cfg.fontFamilyDish).trim() ? resolveFontFamily(cfg.fontFamilyDish) : baseFamily,
    fontSizeDish: cfg.fontSizeDish != null && String(cfg.fontSizeDish).trim() ? resolveFontSize(cfg.fontSizeDish) : `${baseNum + 2}px`,
    fontFamilyDescription: cfg.fontFamilyDescription != null && String(cfg.fontFamilyDescription).trim() ? resolveFontFamily(cfg.fontFamilyDescription) : baseFamily,
    fontSizeDescription: cfg.fontSizeDescription != null && String(cfg.fontSizeDescription).trim() ? resolveFontSize(cfg.fontSizeDescription) : basePx,
    fontFamilyFooter: cfg.fontFamilyFooter != null && String(cfg.fontFamilyFooter).trim() ? resolveFontFamily(cfg.fontFamilyFooter) : baseFamily,
    fontSizeFooter: cfg.fontSizeFooter != null && String(cfg.fontSizeFooter).trim() ? resolveFontSize(cfg.fontSizeFooter) : basePx,
    fontFamilyPrice: cfg.fontFamilyPrice != null && String(cfg.fontFamilyPrice).trim() ? resolveFontFamily(cfg.fontFamilyPrice) : baseFamily,
    fontSizePrice: cfg.fontSizePrice != null && String(cfg.fontSizePrice).trim() ? resolveFontSize(cfg.fontSizePrice) : `${baseNum + 2}px`,
    fontWeightPrice: (cfg.fontWeightPrice as string) || '700',
    primaryColor: resolveColor(
      cfg.primary || cfg.primaryColor || cfg.headerColor || cfg.categoryColor,
      DEFAULT_RESOLVED.primaryColor
    ),
    secondaryColor: resolveColor(
      cfg.secondary || cfg.priceColor || cfg.secondaryColor,
      DEFAULT_RESOLVED.secondaryColor
    ),
    backgroundColor: resolveColor(
      cfg.background || cfg.backgroundColor,
      DEFAULT_RESOLVED.backgroundColor
    ),
    textColor: resolveColor(
      cfg.text || cfg.dishColor || cfg.descriptionColor,
      DEFAULT_RESOLVED.textColor
    ),
    headerColor: resolveColor(
      cfg.header || cfg.headerColor || cfg.categoryColor,
      DEFAULT_RESOLVED.headerColor
    ),
    categoryColor: resolveColor(
      cfg.categoryColor || cfg.headerColor,
      DEFAULT_RESOLVED.categoryColor
    ),
    dishColor: resolveColor(cfg.dishColor || cfg.text, DEFAULT_RESOLVED.dishColor),
    descriptionColor: resolveColor(
      cfg.descriptionColor || cfg.text,
      DEFAULT_RESOLVED.descriptionColor
    ),
    priceColor: resolveColor(cfg.priceColor || cfg.secondary, DEFAULT_RESOLVED.priceColor),
    allergenColor: resolveColor(cfg.allergenColor, DEFAULT_RESOLVED.allergenColor),
    cardBackground: resolveColor(
      cfg.cardBackground || cfg.backgroundColor,
      DEFAULT_RESOLVED.cardBackground
    ),
    cardBorderColor: resolveColor(
      cfg.cardBorderColor || cfg.borderColor,
      DEFAULT_RESOLVED.cardBorderColor
    ),
    cardBorderWidth: (() => {
      const v = cfg.cardBorderWidth ?? cfg.itemBorderWidth ?? 1;
      const n = typeof v === 'number' ? v : parseInt(String(v), 10);
      return (isNaN(n) || n < 0 ? 1 : n) + 'px';
    })(),
    showItemBorder: cfg.showItemBorder !== false,
    borderRadius: BORDER_RADIUS_MAP[String(cfg.borderRadius || '').toLowerCase()] || (cfg.borderRadius && /^\d+(px|%)?$/.test(String(cfg.borderRadius)) ? cfg.borderRadius : '12px'),
    lineHeight: cfg.lineHeight || '1.6',
    showImages: cfg.showImages !== false,
    showPrices: cfg.showPrices !== false,
    showAllergens: cfg.showAllergens !== false,
    alignment: cfg.alignment || 'left',
    theme: (cfg.theme as 'light' | 'dark' | 'custom') || 'light',
    backgroundImage: cfg.backgroundImage || null,
    categoryDisplayMode: (cfg.categoryDisplayMode as 'cloud' | 'scroll') || 'scroll',
    overlayColor: resolveColor(cfg.overlayColor, '#000000'),
    overlayOpacity: Math.min(100, Math.max(0, Number(cfg.overlayOpacity) ?? 0)),
    logoSize: (cfg.logoSize as string) || 'medium',
    fontWeightDish: (cfg.fontWeightDish as string) || '700'
  };
}

/**
 * Convert hex color to rgba string for overlay
 */
export function overlayColorToRgba(hex: string, opacityPercent: number): string {
  if (!hex || opacityPercent <= 0) return 'transparent';
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `rgba(0, 0, 0, ${opacityPercent / 100})`;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${opacityPercent / 100})`;
}

/**
 * Build CSS variables object for use in style prop
 */
export function getStyleVars(config: ResolvedStyleConfig): React.CSSProperties {
  return {
    fontFamily: config.fontFamily,
    fontSize: config.fontSizeBase,
    lineHeight: config.lineHeight,
    color: config.textColor,
    ['--menu-font' as string]: config.fontFamily,
    ['--menu-font-header' as string]: config.fontFamilyHeader ?? config.fontFamily,
    ['--menu-font-size-header' as string]: config.fontSizeHeader ?? config.fontSizeXLarge,
    ['--menu-font-macro' as string]: config.fontFamilyMacroSection ?? config.fontFamily,
    ['--menu-font-size-macro' as string]: config.fontSizeMacroSection ?? config.fontSizeXLarge,
    ['--menu-font-category' as string]: config.fontFamilyCategory ?? config.fontFamily,
    ['--menu-font-size-category' as string]: config.fontSizeCategory ?? config.fontSizeSmall,
    ['--menu-font-dish' as string]: config.fontFamilyDish ?? config.fontFamily,
    ['--menu-font-size-dish' as string]: config.fontSizeDish ?? config.fontSizeLarge,
    ['--menu-font-description' as string]: config.fontFamilyDescription ?? config.fontFamily,
    ['--menu-font-size-description' as string]: config.fontSizeDescription ?? config.fontSizeBase,
    ['--menu-font-footer' as string]: config.fontFamilyFooter ?? config.fontFamily,
    ['--menu-font-size-footer' as string]: config.fontSizeFooter ?? config.fontSizeBase,
    ['--menu-font-price' as string]: config.fontFamilyPrice ?? config.fontFamilyDish ?? config.fontFamily,
    ['--menu-font-size-price' as string]: config.fontSizePrice ?? config.fontSizeDish ?? config.fontSizeLarge,
    ['--menu-font-weight-price' as string]: config.fontWeightPrice ?? '700',
    ['--menu-primary' as string]: config.primaryColor,
    ['--menu-secondary' as string]: config.secondaryColor,
    ['--menu-bg' as string]: config.backgroundColor,
    ['--menu-text' as string]: config.textColor,
    ['--menu-header' as string]: config.headerColor,
    ['--menu-category' as string]: config.categoryColor,
    ['--menu-dish' as string]: config.dishColor,
    ['--menu-description' as string]: config.descriptionColor,
    ['--menu-price' as string]: config.priceColor,
    ['--menu-allergen' as string]: config.allergenColor,
    ['--menu-card-bg' as string]: config.cardBackground,
    ['--menu-radius' as string]: config.borderRadius,
    ['--menu-logo-size' as string]: config.logoSize || 'medium',
    ['--menu-font-weight-dish' as string]: config.fontWeightDish || '700'
  };
}
